<script setup lang="ts">
import { isRewardAppOnlyBlocked } from '@/utils/rewards';

type Props = {
  selectedReward: Reward;
};

const { selectedReward } = defineProps<Props>();

const modelValue = defineModel<boolean>({
  required: true,
});

// Reemitido pra quem embute esta lista dentro de um painel próprio (o sheet de
// Rewards do Clube Fidelidade) — ver handlePlayNow abaixo.
const emit = defineEmits<{ play: [] }>();

const isLoadingRedeem = ref(false);
const isRedeemSuccess = ref(false);

const { t } = useTexts();
const { redeemReward } = useRewards();
const { openToaster } = useLayoutStore();
// Cartaz próprio do bônus app-only (`components/app-download/BonusModal.vue`),
// não o genérico "Baixe o aplicativo" do menu lateral (ENG-495).
const appDownload = useAppDownloadBonusStore();

// ENG-799: com o botão do card travado este modal não abre pelo caminho
// normal. Existe pra abertura programática — e porque o guard do
// `redeemReward()` engoliria o "Confirmar" em silêncio.
const isAppOnlyBlocked = computed<boolean>(
  () => !!selectedReward && isRewardAppOnlyBlocked(selectedReward),
);

async function handleRedeemReward() {
  if (!selectedReward) {
    return;
  }

  isLoadingRedeem.value = true;

  await redeemReward(selectedReward.id)
    .then((response) => {
      if (response?.data?.success) {
        isRedeemSuccess.value = true;

        return;
      }

      // Mesmo motivo do modal do V2: 2xx com `success: false` não cai no
      // catch, e sem este ramo a recusa do backend ficava muda.
      openToaster({
        message:
          response?.data?.message ||
          response?.data?.user_message ||
          t('rewards.redeem_error'),
        type: 'error',
      });
    })
    .catch((error: any) => {
      const message =
        error?.data?.message ||
        error?.data?.user_message ||
        t('rewards.redeem_error');

      openToaster({
        message,
        type: 'error',
      });

      isRedeemSuccess.value = false;
    })
    .finally(() => {
      isLoadingRedeem.value = false;
    });
}

function handleCloseRedeemSuccess() {
  isRedeemSuccess.value = false;
  modelValue.value = false;
}

// "Jogar Agora" navega pra fora (jogo ou /sports) no mesmo clique: o NuxtLink
// só troca de rota, não fecha nada. Aqui fechamos o BaseModal do resgate e
// avisamos o pai — dentro do Clube Fidelidade este modal vive fora da página
// roteada (Pinia + Teleport), então nada desmontava e ele ficava por cima do
// jogo (ENG-815). Mesmo tratamento que o caminho V2 já fazia.
function handlePlayNow() {
  handleCloseRedeemSuccess();
  emit('play');
}
</script>

<template>
  <div :class="$style.modalRedeem">
    <BaseLoading
      class="!bg-auth-bg-primary"
      :show="isLoadingRedeem"
    />

    <UserRewardsRedeemSuccess
      v-if="isRedeemSuccess"
      :selected-reward="selectedReward"
      @close="handleCloseRedeemSuccess"
      @play="handlePlayNow"
    />

    <template v-else>
      <p
        v-if="isAppOnlyBlocked"
        :class="$style.appOnlyText"
      >
        {{ $t('rewards.app_only.badge') }}
      </p>

      <p
        v-else
        :class="$style.confirmText"
      >
        <NuxtIcon
          :class="$style.confirmTextIcon"
          name="check"
        />

        <strong>
          {{ $t('rewards.redeem_confirm') }}
        </strong>
      </p>

      <div :class="$style.itemDetail">
        <UserRewardsItem
          :key="selectedReward?.id"
          :big-card="true"
          :button-visible="false"
          :reward="selectedReward"
          :status-visible="false"
        />
      </div>

      <!-- ENG-799: bloqueado troca "Confirmar" pelo convite de baixar o app. -->
      <div :class="$style.confirmationButtons">
        <button
          v-if="isAppOnlyBlocked"
          :class="$style.appOnlyCta"
          @click="appDownload.open()"
        >
          <NuxtIcon name="download" />
          {{ $t('rewards.app_only.cta') }}
        </button>

        <button
          v-else
          class="step-actions-btn step-actions-btn-true"
          @click="handleRedeemReward()"
        >
          {{ $t('general.confirm') }}
        </button>

        <button
          class="step-actions-btn step-actions-btn-false"
          @click="modelValue = false"
        >
          {{ isAppOnlyBlocked ? $t('general.close') : $t('general.cancel') }}
        </button>
      </div>
    </template>
  </div>
</template>

<style module lang="postcss">
.modalRedeem {
  @apply text-center p-10;

  .itemDetail {
    @apply text-left w-full my-6;
  }

  .confirmText {
    @apply text-center flex gap-2 items-center justify-center;

    .confirmTextIcon {
      @apply text-xl text-success w-8 h-8 rounded-full bg-success/10 flex items-center justify-center;
    }

    strong {
      @apply text-2xl font-bold text-texts;
    }
  }

  /* ENG-799: aviso e CTA do bônus app-only, no mesmo `--v2-gold` dos cards
     (token global, no :root via main.scss). */
  .appOnlyText {
    @apply text-lg font-extrabold tracking-wide text-v2-gold bg-v2-gold/10 border border-solid border-v2-gold/30 rounded-md py-3 px-4;
  }

  .confirmationButtons {
    @apply grid grid-cols-2 gap-2;

    button {
      @apply py-2 rounded-md capitalize font-semibold;

      &:first-child {
        @apply bg-success text-white;
      }
      &:last-child {
        @apply bg-error text-white;
      }

      &:disabled {
        @apply opacity-40 cursor-not-allowed;
      }

      /* ENG-799: precisa vir DEPOIS de &:first-child e com a mesma
         especificidade (.confirmationButtons button.appOnlyCta), senão o
         verde de confirmação ganha por ordem de origem — o CTA virou
         <button> quando passou a abrir o modal de download, e aí passou a
         casar com o seletor `button` acima. `normal-case` porque o
         `capitalize` do bloco transformava "Baixar o app" em "Baixar O App". */
      &.appOnlyCta {
        @apply flex items-center justify-center gap-1 normal-case bg-v2-gold text-v2-background;
      }
    }
  }
}
</style>
